import { Typography, Box, Divider, Stack, Button, Checkbox, FormControlLabel } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Select as AntdSelect } from 'antd';
import { SuffixIcon, TagRenderer } from '@med-fe/ui';
import {
  fetchHolidaysFull,
  fetchHolidaysSome,
  fetchRegions,
  fetchDistricts,
  fetchHolidayLocations,
  fetchHolidayPartialLocations,
} from './saga';
import GenericGrid from '../grid/GenericGrid/GenericGrid';
import { updateHolidaySchedule } from './actions';
import { useDispatch } from 'react-redux';

export default function HolidaySchedule() {
  const dispatch = useDispatch();
  const [apiHolidays, setApiHolidays] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>('2026'); // Default year
  const [selectedPartialHolidays, setSelectedPartialHolidays] = useState<string[]>([]);
  const [partialHolidayOptions, setPartialHolidayOptions] = useState<
    {
      label: string;
      value: string;
      holidaySid?: number;
      holidayCalendarId?: number;
      holidayYear?: number;
    }[]
  >([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedRegionObjs, setSelectedRegionObjs] = useState<{ id: number; name: string }[]>([]);
  const [selectedDistrictObjs, setSelectedDistrictObjs] = useState<
    { label: string; value: string; regionId?: number; districtId?: number }[]
  >([]);
  const [selectedLocationObjs, setSelectedLocationObjs] = useState<
    {
      label: string;
      value: string;
      districtSid?: number;
      regionSid?: number;
      locationSid?: number;
      locationName?: string;
    }[]
  >([]);
  const [locationMap, setLocationMap] = useState<
    Record<string, { regionSid?: number; districtSid?: number; locationSid?: number; locationName?: string }>
  >({});
  const [regions, setRegions] = useState<{ id: number; name: string }[]>([]);
  const [districtOptions, setDistrictOptions] = useState<
    { label: string; value: string; regionId?: number; districtId?: number }[]
  >([]);
  const [locationOptions, setLocationOptions] = useState<
    { label: string; value: string; districtId?: number; regionId?: number }[]
  >([]);
  const [selectAllRegions, setSelectAllRegions] = useState<boolean>(false);
  const [selectAllDistricts, setSelectAllDistricts] = useState<boolean>(false);
  const [selectAllLocations, setSelectAllLocations] = useState<boolean>(false);
  const [holidayGridRows, setHolidayGridRows] = useState<
    {
      holidayId?: number | string;
      holidayName?: string;
      holidayDate?: string;
      locationNumber?: string;
      locationName?: string;
      regionName?: string;
      districtName?: string;
    }[]
  >([]);

  const years = ['2026', '2027']; // You can extend this dynamically

  // locations are loaded from API based on selected region + districts

  const formatDateOnly = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toISOString().split('T')[0];
    } catch {
      return String(dateStr).split('T')[0] || String(dateStr);
    }
  };

  const holidayColumns = [
    {
      id: 'HolidayCalendarId',
      header: 'HolidayId',
      accessorKey: 'holidayId',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 120,
    },
    {
      id: 'HolidayName',
      header: 'HolidayName',
      accessorKey: 'holidayName',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 220,
    },
    {
      id: 'HolidayDate',
      header: 'HolidayDate',
      accessorKey: 'holidayDate',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 140,
      Cell: ({ row }: any) => (row?.original?.holidayDate ? row.original.holidayDate : ''),
    },
    {
      id: 'LocationNumber',
      header: 'LocationNumber',
      accessorKey: 'locationNumber',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 140,
    },
    {
      id: 'LocationName',
      header: 'LocationName',
      accessorKey: 'locationName',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 220,
    },
    {
      id: 'RegionName',
      header: 'Region',
      accessorKey: 'regionName',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 160,
    },
    {
      id: 'DistrictName',
      header: 'District',
      accessorKey: 'districtName',
      enableColumnFilter: true,
      filterFn: 'contains',
      size: 160,
    },
  ];

  useEffect(() => {
    let mounted = true;

    fetchHolidaysFull()
      .then((res: any) => {
        if (!mounted) return;

        if (Array.isArray(res)) {
          const mapped = res
            .map((item: any) => {
              const name = item.HolidayName || item.Name || item.name || '';
              const rawDate = item.HolidayDate || item.Date || item.date || '';

              if (!name) return null;

              const date = rawDate ? new Date(rawDate).toISOString().split('T')[0] : '';

              return date ? `${name} (${date})` : name;
            })
            .filter(Boolean) as string[];

          setApiHolidays(mapped);
        }
      })
      .catch(() => {});

    // ✅ Pass selectedYear here
    fetchHolidaysSome(selectedYear).then((res: any) => {
      if (!mounted) return;

      if (Array.isArray(res)) {
        const opts = res
          .map((item: any) => {
            const name = item.HolidayName || item.Name || item.name || '';
            const rawDate = item.HolidayDate || item.Date || item.date || '';
            const date = rawDate ? new Date(rawDate).toISOString().split('T')[0] : '';

            const label = name ? `${name}${date ? ` (${date})` : ''}` : '';

            const holidaySid = item.HolidaySid || item.HolidayId || item.HolidayID || item.id || item.Id || null;

            const value = holidaySid != null ? String(holidaySid) : label;

            return label
              ? {
                  label,
                  value,
                  holidaySid: holidaySid != null ? Number(holidaySid) : undefined,
                  holidayCalendarId: item.HolidayCalendarId,
                  holidayYear: item.HolidayYear,
                }
              : null;
          })
          .filter(Boolean) as {
          label: string;
          value: string;
          holidaySid?: number;
          holidayCalendarId?: number;
          holidayYear?: number;
        }[];

        setPartialHolidayOptions(opts);
      }
    });

    fetchHolidayPartialLocations(selectedYear)
      .then((data: any[]) => {
        const rows: any[] = [];
        (data || []).forEach((item: any) => {
          const hid = item.HolidayCalendarId ?? item.HolidaySid ?? item.HolidayId ?? item.id ?? item.Id ?? '';
          const name = item.HolidayName || item.Name || item.name || '';
          const date = formatDateOnly(item.HolidayDate || item.Date || item.date);
          const locations = Array.isArray(item.Locations) ? item.Locations : [];
          if (locations.length > 0) {
            locations.forEach((loc: any) => {
              rows.push({
                holidayId: hid,
                holidayName: name,
                holidayDate: date,
                locationNumber: loc.locationNumber || loc.number || loc.LocationNumber || loc.Code || '',
                locationName: loc.locationName || loc.name || loc.LocationName || '',
                regionName: loc.regionName || loc.RegionName || '',
                districtName: loc.districtName || loc.DistrictName || '',
              });
            });
          }
        });
        setHolidayGridRows(rows);
      })
      .catch(() => {
        setHolidayGridRows([]);
      });

    fetchRegions()
      .then((res: any) => {
        if (!mounted) return;
        if (Array.isArray(res)) {
          setRegions(res.map((r: any) => ({ id: r.id, name: r.name })));
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [selectedYear]);
  useEffect(() => {
    let mounted = true;
    if (!selectedRegions || selectedRegions.length === 0) {
      setDistrictOptions([]);
      return;
    }

    const formatDateOnly = (dateStr?: string) => {
      if (!dateStr) return '';
      return new Date(dateStr).toISOString().split('T')[0];
    };

    const regionIds = selectedRegions;
    Promise.all(regionIds.map((id) => fetchDistricts(id)))
      .then((results: any[]) => {
        if (!mounted) return;
        const combined = results.flat();
        const opts = (combined || [])
          .map((d: any) => ({ label: d.name || d.Name || '', value: String(d.id), regionId: d.regionId || d.RegionId }))
          .filter((o: any) => o.label);
        const unique = Array.from(new Map(opts.map((o: any) => [o.value, o])).values());
        setDistrictOptions(unique);
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [selectedRegions]);

  // Select All regions -> select/clear regions
  useEffect(() => {
    if (selectAllRegions) {
      const all = regions.map((r) => String(r.id));
      setSelectedRegions(all);
      setSelectedRegionObjs(regions);
    }
  }, [selectAllRegions, regions]);

  useEffect(() => {
    const allRegionsSelected = regions.length > 0 && selectedRegions.length === regions.length;
    setSelectAllRegions(allRegionsSelected);
  }, [regions, selectedRegions]);

  // Select All districts -> select/clear districts
  useEffect(() => {
    if (selectAllDistricts) {
      const all = districtOptions.map((d) => d.value);
      setSelectedDistricts(all);
      setSelectedDistrictObjs(districtOptions);
    }
  }, [selectAllDistricts, districtOptions]);

  useEffect(() => {
    const allDistrictsSelected = districtOptions.length > 0 && selectedDistricts.length === districtOptions.length;
    setSelectAllDistricts(allDistrictsSelected);
  }, [districtOptions, selectedDistricts]);

  // Select All locations -> select/clear locations
  useEffect(() => {
    if (selectAllLocations) {
      const all = locationOptions.map((o) => o.value);
      setSelectedLocations(all);
      setSelectedLocationObjs(locationOptions as any);
    }
  }, [selectAllLocations, locationOptions]);

  useEffect(() => {
    const allLocationsSelected = locationOptions.length > 0 && selectedLocations.length === locationOptions.length;
    setSelectAllLocations(allLocationsSelected);
  }, [locationOptions, selectedLocations]);

  useEffect(() => {
    let mounted = true;
    if (!selectedRegions || selectedRegions.length === 0) {
      setLocationOptions([]);
      return;
    }

    // Map selected districts to their region and prepare requests per region
    const regionMap = new Map<string, string[]>();
    selectedRegions.forEach((rid) => regionMap.set(rid, []));

    selectedDistricts.forEach((did) => {
      const found = districtOptions.find((d) => d.value === did);
      if (found && found.regionId != null) {
        const rid = String(found.regionId);
        if (!regionMap.has(rid)) regionMap.set(rid, []);
        regionMap.get(rid)!.push(did);
      }
    });

    const requests = Array.from(regionMap.entries())
      .filter(([, dids]) => dids && dids.length > 0)
      .map(([rid, dids]) => fetchHolidayLocations({ RegionId: Number(rid), DistrictIds: dids.map(Number) }));

    if (requests.length === 0) {
      setLocationOptions([]);
      return;
    }

    Promise.all(requests)
      .then((results: any[]) => {
        if (!mounted) return;
        const combined = results.flat();
        const opts = (combined || [])
          .map((l: any) => {
            const locationName = l.LocationName || l.Location || l.name || l.Name || '';
            const locationNumber = l.LocationNumber || l.Code || '';
            const label =
              locationName && locationNumber
                ? `${locationName} (${locationNumber})`
                : locationName || locationNumber || '';
            const value = String(l.LocationSid || l.id || l.Id || label);
            return {
              label,
              value,
              locationSid: l.LocationSid || l.LocationId || l.id || l.Id,
              districtSid: l.DistrictSid || l.DistrictId || l.districtId || (l.District && l.District.id),
              regionSid: l.RegionSid || l.RegionId || l.regionId || (l.Region && l.Region.id),
              locationName,
              locationNumber,
            };
          })
          .filter((o: any) => o.label);
        const unique = Array.from(new Map(opts.map((o: any) => [o.value, o])).values());
        const mapObj: Record<
          string,
          { regionSid?: number; districtSid?: number; locationSid?: number; locationName?: string }
        > = {};
        unique.forEach((u: any) => {
          mapObj[String(u.value)] = {
            regionSid: u.regionSid != null ? Number(u.regionSid) : undefined,
            districtSid: u.districtSid != null ? Number(u.districtSid) : undefined,
            locationSid: u.locationSid != null ? Number(u.locationSid) : undefined,
            locationName: u.locationName || u.label,
          };
        });
        setLocationMap((prev) => ({ ...prev, ...mapObj }));
        setLocationOptions(unique);
      })
      .catch(() => {});

    return () => {
      mounted = false;
    };
  }, [selectedRegions, selectedDistricts, districtOptions]);

  const handleUpdate = () => {
    const holidays = selectedPartialHolidays
      .map((val) => {
        const opt = partialHolidayOptions.find((o) => o.value === val || o.label === val);

        return {
          holidayId: opt?.holidaySid != null ? Number(opt.holidaySid) : !isNaN(Number(val)) ? Number(val) : undefined,
          holidayName: opt?.label || val,
          holidayCalendarId: opt?.holidayCalendarId,
          holidayYear: opt?.holidayYear,
        };
      })
      .filter((h) => h.holidayName);

    const locations = selectedLocationObjs
      .map((opt) => {
        const locationSid =
          opt?.locationSid != null
            ? Number(opt.locationSid)
            : !isNaN(Number(opt.value))
            ? Number(opt.value)
            : undefined;

        const locationName = opt?.locationName || opt?.label || String(opt.value);

        const regionSid =
          opt?.regionSid != null
            ? Number(opt.regionSid)
            : locationSid != null
            ? locationMap[String(locationSid)]?.regionSid
            : undefined;

        const districtSid =
          opt?.districtSid != null
            ? Number(opt.districtSid)
            : locationSid != null
            ? locationMap[String(locationSid)]?.districtSid
            : undefined;

        return {
          locationId: locationSid,
          locationName,
          regionId: regionSid,
          districtId: districtSid,
        };
      })
      .filter((l) => l.locationName);

    const payload = {
      holidays,
      locations,
    };

    // ✅ Dispatch saga action
    dispatch(updateHolidaySchedule(payload));
  };

  return (
    <Box className='urc-container' sx={{ p: 2 }}>
      <Typography variant='h5' fontWeight={600} gutterBottom>
        Holiday Schedule
      </Typography>

      <Divider sx={{ mb: 2 }} />

      {/* Fully Observed Holidays */}
      <Typography variant='h6' fontWeight={700} sx={{ mb: 3 }}>
        Fully Observed Holidays
      </Typography>
      <Stack spacing={1} sx={{ mb: 4 }}>
        {apiHolidays.map((holiday) => (
          <Typography key={`full-${holiday}`} variant='body1'>
            {holiday}
          </Typography>
        ))}
      </Stack>

      {/* Partially Observed Holidays */}
      <Typography variant='h6' fontWeight={700} sx={{ mb: 2 }}>
        Partially Observed Holidays
      </Typography>
      {/* Year Selector */}
      <div style={{ minWidth: 160, width: 180, marginBottom: -24, marginTop: 8 }}>
        <AntdSelect
          value={selectedYear}
          onChange={(val) => setSelectedYear(val as string)}
          style={{ width: '100%' }}
          allowClear
          suffixIcon={<SuffixIcon />}
          options={years.map((y) => ({ label: y, value: y }))}
          placeholder='Select year'
          showSearch
          filterOption={(input, option) => (option?.label || '').toString().toLowerCase().includes(input.toLowerCase())}
        />
      </div>

      <Stack direction='row' spacing={6} alignItems='flex-start' sx={{ mb: 2, flexWrap: 'wrap' }}>
        {/* Holidays */}
        <div style={{ minWidth: 160, width: 180 }}>
          <div style={{ height: 55 }} />
          <AntdSelect
            mode='multiple'
            allowClear
            value={selectedPartialHolidays}
            onChange={(vals) => setSelectedPartialHolidays(vals as string[])}
            maxTagCount={'responsive'}
            tagRender={(props) => <TagRenderer {...props} />}
            suffixIcon={<SuffixIcon />}
            style={{ width: '100%' }}
            options={partialHolidayOptions}
            placeholder='Select holiday'
          />
        </div>

        {/* Regions */}
        <div style={{ minWidth: 160, width: 160 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllRegions}
                onChange={(e) => setSelectAllRegions(Boolean(e.target.checked))}
                size='small'
              />
            }
            label='Select All Regions'
            sx={{ mb: 1 }}
          />
          <AntdSelect
            mode='multiple'
            allowClear
            value={selectedRegions}
            onChange={(vals) => {
              const values = vals as string[];
              setSelectedRegions(values);
              const objs = values.map((v) => regions.find((r) => String(r.id) === v)).filter(Boolean) as {
                id: number;
                name: string;
              }[];
              setSelectedRegionObjs(objs);
            }}
            maxTagCount={1}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
            tagRender={(props) => <TagRenderer {...props} />}
            suffixIcon={<SuffixIcon />}
            style={{ width: '100%' }}
            options={regions.map((r) => ({ label: r.name, value: String(r.id) }))}
            placeholder='Select regions'
            showSearch
            filterOption={(input, option) =>
              (option?.label || '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {/* Districts */}
        <div style={{ minWidth: 160, width: 160 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllDistricts}
                onChange={(e) => setSelectAllDistricts(Boolean(e.target.checked))}
                size='small'
              />
            }
            label='Select All Districts'
            sx={{ mb: 1 }}
          />
          <AntdSelect
            mode='multiple'
            allowClear
            value={selectedDistricts}
            onChange={(vals) => {
              const values = vals as string[];
              setSelectedDistricts(values);
              const objs = values.map((v) => districtOptions.find((d) => d.value === v)).filter(Boolean) as {
                label: string;
                value: string;
                regionId?: number;
                districtId?: number;
              }[];
              setSelectedDistrictObjs(objs);
            }}
            maxTagCount={1}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
            tagRender={(props) => <TagRenderer {...props} />}
            suffixIcon={<SuffixIcon />}
            style={{ width: '100%' }}
            options={districtOptions}
            placeholder='Select districts'
            showSearch
            filterOption={(input, option) =>
              (option?.label || '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        {/* Locations */}
        <div style={{ minWidth: 160 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectAllLocations}
                onChange={(e) => setSelectAllLocations(Boolean(e.target.checked))}
                size='small'
              />
            }
            label='Select All Locations'
            sx={{ mb: 1 }}
          />
          <AntdSelect
            mode='multiple'
            allowClear
            value={selectedLocations}
            onChange={(vals) => {
              const values = vals as string[];
              setSelectedLocations(values);
              const objs = values.map((v) => locationOptions.find((o) => o.value === v)).filter(Boolean) as {
                label: string;
                value: string;
                districtId?: number;
                regionId?: number;
              }[];
              setSelectedLocationObjs(objs);
            }}
            maxTagCount={1}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
            tagRender={(props) => <TagRenderer {...props} />}
            suffixIcon={<SuffixIcon />}
            style={{ width: '100%' }}
            options={locationOptions}
            placeholder='Select locations'
            showSearch
            filterOption={(input, option) =>
              (option?.label || '').toString().toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>
      </Stack>

      <Button
        variant='contained'
        color='primary'
        onClick={handleUpdate}
        disabled={
          selectedPartialHolidays.length === 0 ||
          selectedRegions.length === 0 ||
          selectedDistricts.length === 0 ||
          selectedLocations.length === 0
        }
        sx={{ mt: 5 }}
      >
        Update
      </Button>
      <Box sx={{ height: 32 }} />
      <GenericGrid
        columns={holidayColumns}
        columnOrder={holidayColumns.map((c) => (c.id || c.accessorKey) as string)}
        data={holidayGridRows}
        sorting={[
          {
            id: 'HolidayDate',
            desc: false,
          },
        ]}
        enableRowSelection={false}
        rowSelection={[]}
        columnPinning={{}}
        filterDisplayMode='popover'
        highlightFilterMatch={true}
        paginationDisplayMode='pages'
        paginationProps={{
          color: 'primary',
          shape: 'rounded',
          showRowsPerPage: true,
          variant: 'outlined',
          rowsPerPageOptions: [10, 50, 100],
        }}
        enableStickyHeader={true}
        enableStickyFooter={false}
        enableColumnPinning={true}
        isLoading={false}
        enableAdd={false}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '11px',
            textAlign: 'left',
            padding: '6px 10px',
            height: '40px',
            lineHeight: '1.2',
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontSize: '11px',
            fontWeight: 700,
            textAlign: 'center',
            padding: '6px 10px',
            height: '40px',
            lineHeight: '1.2',
          },
        }}
        muiTableBodyRowProps={{
          sx: {
            height: '40px',
            '& td': {
              padding: '6px 10px',
              height: '40px',
            },
            textAlign: 'center',
          },
        }}
      />
    </Box>
  );
}
